import { DBSchema, IDBPDatabase, openDB } from 'idb';
import { createContext, useContext, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import { ObservableError } from '../domain/errors/observable.error';
import { JobReviewOfflineStorageChangeEvent } from '../domain/events/JobReviewOfflineStorageChangeEvent';
import { useEventBus } from '../hooks/useEventBus';
import { EFReviewRowModel } from '../pages/job/components/models/ef-review';
import { OfflineStorage } from '../ports/offline-storage';
import { DomEventBus } from './dom.event.bus';

const REVIEW_STORAGE_NAME = 'JobReview';
const REVIEW_COLLECTION_NAME = 'reviews';

export type ReviewDocument = {
    id: string;
    state: Record<number, EFReviewRowModel>;
    timestamp: string;
};

interface ReviewSchema extends DBSchema {
    [REVIEW_COLLECTION_NAME]: {
        key: string;
        value: ReviewDocument;
    };
}

export class NullReviewOfflineStorage implements OfflineStorage<ReviewDocument> {
    async read(_key: string): Promise<ReviewDocument | null> {
        return null;
    }
    async write(_document: ReviewDocument): Promise<void> {
        /* noop */
    }
    async remove(_key: string): Promise<void> {
        /* noop */
    }
    isReady(): boolean {
        return false;
    }
}

export class DisabledReviewOfflineStorage extends NullReviewOfflineStorage {
    isReady(): boolean {
        return true;
    }
}

export class ReviewOfflineStorage implements OfflineStorage<ReviewDocument> {
    private constructor(
        private readonly connection: IDBPDatabase<ReviewSchema>,
        private readonly eventBus: DomEventBus
    ) {}

    static async factory(eventBus: DomEventBus): Promise<ReviewOfflineStorage> {
        try {
            const connection = await openDB<ReviewSchema>(REVIEW_STORAGE_NAME, 1, {
                upgrade(db) {
                    db.createObjectStore(REVIEW_COLLECTION_NAME, {
                        keyPath: 'id',
                    });
                },
            });
            return new ReviewOfflineStorage(connection, eventBus);
        } catch {
            throw new ObservableError('OfflineDB: Connection failed', { recoverable: true });
        }
    }

    async read(key: string) {
        try {
            const doc = await this.connection.get(REVIEW_COLLECTION_NAME, key);
            if (!doc) {
                return null;
            }
            return doc;
        } catch {
            return null;
        }
    }

    async write(doc: ReviewDocument) {
        try {
            await this.connection.put(REVIEW_COLLECTION_NAME, doc);
            this.eventBus.emit(new JobReviewOfflineStorageChangeEvent({ jobId: doc.id }));
        } catch {
            throw new ObservableError('OfflineDB: Write operation failed');
        }
    }

    async remove(key: string) {
        try {
            await this.connection.delete(REVIEW_COLLECTION_NAME, key);
            this.eventBus.emit(new JobReviewOfflineStorageChangeEvent({ jobId: key }));
        } catch {
            throw new ObservableError('OfflineDB: Remove operation failed');
        }
    }

    isReady() {
        return true;
    }
}

const ReviewOfflineStorageContext = createContext<OfflineStorage<ReviewDocument>>(new NullReviewOfflineStorage());

export const ReviewOfflineStorageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { eventBus } = useEventBus();
    const { showBoundary } = useErrorBoundary();
    const [storage, setStorage] = useState<OfflineStorage<ReviewDocument>>(new NullReviewOfflineStorage());

    useEffect(() => {
        const init = async () => {
            try {
                setStorage(await ReviewOfflineStorage.factory(eventBus));
            } catch (error) {
                showBoundary(error);
                setStorage(new DisabledReviewOfflineStorage());
            }
        };

        if (!storage.isReady()) {
            init();
        }
    }, [eventBus, showBoundary, storage]);

    return <ReviewOfflineStorageContext.Provider value={storage}>{children}</ReviewOfflineStorageContext.Provider>;
};

export const useReviewOfflineStorage = (): OfflineStorage<ReviewDocument> => {
    return useContext(ReviewOfflineStorageContext);
};
